import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { playErrorNotification, showBrowserNotification } from '../utils/notifications';
import { debugLog, debugError } from '../utils/debug';
import './AudioPlayer.css';

const AudioPlayer = React.forwardRef(({ audioUrl, currentTime, onTimeUpdate, audioFile, onLoadingChange, onPlay, onPause, skipDuration = 10 }, ref) => {

  debugLog('[DEBUG AudioPlayer component rendered with props:', {
    audioUrl,
    audioUrlType: typeof audioUrl,
    currentTime,
    audioFilePresent: !!audioFile,
    audioFileName: audioFile?.name
  });

  // Debug logging function
  const logAudioState = useCallback((event = 'manual check') => {
    const audio = audioRef?.current;
    if (!audio) {
      // console.log(`[AUDIO DEBUG] ${event}: No audio element`);
      return;
    }

    const state = {
      event,
      src: audio.src || 'not set',
      currentSrc: audio.currentSrc || 'not set',
      readyState: audio.readyState,
      readyStateText: ['HAVE_NOTHING', 'HAVE_METADATA', 'HAVE_CURRENT_DATA', 'HAVE_FUTURE_DATA', 'HAVE_ENOUGH_DATA'][audio.readyState] || 'unknown',
      networkState: audio.networkState,
      networkStateText: ['NETWORK_EMPTY', 'NETWORK_IDLE', 'NETWORK_LOADING', 'NETWORK_NO_SOURCE'][audio.networkState] || 'unknown',
      duration: audio.duration,
      currentTime: audio.currentTime,
      paused: audio.paused,
      ended: audio.ended,
      error: audio.error ? {
        code: audio.error.code,
        message: audio.error.message
      } : null,
      bufferedRanges: audio.buffered.length > 0 ? {
        start: audio.buffered.start(0),
        end: audio.buffered.end(0),
        length: audio.buffered.length
      } : 'none',
      seekableRanges: audio.seekable.length > 0 ? {
        start: audio.seekable.start(0),
        end: audio.seekable.end(0),
        length: audio.seekable.length
      } : 'none'
    };

    // console.log(`[AUDIO DEBUG] ${event}:`, state);
    return state;
  }, []);

  // Expose debugging functions to global scope
  useEffect(() => {
    window.debugAudio = {
      logState: () => logAudioState('manual debug'),
      testPlay: async () => {
        const audio = audioRef?.current;
        if (!audio) {
          // debugLog('[AUDIO DEBUG No audio element for play test');
          return;
        }
        try {
          // debugLog('[AUDIO DEBUG Testing manual play...');
          await audio.play();
          // debugLog('[AUDIO DEBUG ✅ Manual play succeeded');
        } catch (error) {
          // debugLog('[AUDIO DEBUG ❌ Manual play failed:', error);
        }
      },
      testBlobUrl: async () => {
        if (!audioUrl) {
          // debugLog('[AUDIO DEBUG No audioUrl to test');
          return;
        }
        try {
          // debugLog('[AUDIO DEBUG Testing blob URL creation...');
          const token = localStorage.getItem('authToken');
          const response = await fetch(audioUrl, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          // debugLog('[AUDIO DEBUG Fetch response:', {
          //   ok: response.ok,
          //   status: response.status,
          //   contentType: response.headers.get('content-type'),
          //   contentLength: response.headers.get('content-length')
          // });
          if (response.ok) {
            const blob = await response.blob();
            // debugLog('[AUDIO DEBUG Blob created:', {
            //   size: blob.size,
            //   type: blob.type
            // });
            const blobUrl = URL.createObjectURL(blob);
            // debugLog('[AUDIO DEBUG Blob URL:', blobUrl);
            const testAudio = new Audio(blobUrl);
            testAudio.addEventListener('loadeddata', () => {
              // debugLog('[AUDIO DEBUG ✅ Test audio loaded from blob');
            });
            testAudio.addEventListener('error', (e) => {
              // debugLog('[AUDIO DEBUG ❌ Test audio failed from blob:', e);
            });
            testAudio.load();
          }
        } catch (error) {
          // debugLog('[AUDIO DEBUG Blob test failed:', error);
        }
      },
      checkSupport: () => {
        const audio = new Audio();
        const support = {
          mp3: audio.canPlayType('audio/mpeg'),
          wav: audio.canPlayType('audio/wav'),
          ogg: audio.canPlayType('audio/ogg'),
          m4a: audio.canPlayType('audio/mp4'),
          webm: audio.canPlayType('audio/webm')
        };
        // debugLog('[AUDIO DEBUG Browser support:', support);
        return support;
      }
    };

    return () => {
      delete window.debugAudio;
    };
  }, [audioUrl, logAudioState]);
  const audioRef = ref;
  const imperativeAudioRef = useRef(null);
  // Create a stable ID that never changes
  const audioElementIdRef = useRef(`audio-player-${Math.random().toString(36).substr(2, 9)}`);
  const audioElementId = audioElementIdRef.current;
  const [audioElementReady, setAudioElementReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const errorTimeoutRef = useRef(null);

  // Notify parent when loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);

  // Handle delayed error display to prevent flashing errors
  useEffect(() => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    if (audioError && !isLoading) {
      // Delay showing errors to prevent brief flashes during successful loads
      errorTimeoutRef.current = setTimeout(() => {
        setShowError(true);
      }, 500); // 500ms delay
    } else {
      setShowError(false);
    }

    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, [audioError, isLoading]);
  const [loadingTimeout, setLoadingTimeout] = useState(null);
  const [lastLoadedUrl, setLastLoadedUrl] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Initialize loading state when URL changes and start loading process
  useEffect(() => {
    if (!audioUrl) {
      setAudioError(null);
      setIsLoading(false);
      return;
    }

    // Wait for imperative audio element to be created
    const waitForAudioElement = () => {
      const audio = imperativeAudioRef.current || audioRef.current;

      if (!audio) {
        debugLog('[DEBUG Waiting for audio element to be created...');
        // Try again in a short while
        setTimeout(waitForAudioElement, 100);
        return;
      }

      // Double-check the element is actually in the DOM
      const domElement = document.getElementById(audioElementId);
      if (!domElement) {
        debugLog('[DEBUG Audio element not found in DOM, waiting...');
        setTimeout(waitForAudioElement, 100);
        return;
      }

      debugLog('[DEBUG Audio element found, starting loading process');

      debugLog('[DEBUG AudioPlayer URL change detected:', {
        audioUrl,
        audioUrlType: typeof audioUrl,
        audioUrlLength: audioUrl?.length,
        lastLoadedUrl,
        currentSrc: audio.currentSrc,
        audioSrc: audio.src,
        readyState: audio.readyState
      });

      // Actually start the loading process
      startLoadingProcess();

      // Ensure event handlers are attached to this audio element
      attachEventHandlers(audio);
    };

    // Get audio element reference for all checks
    const audio = imperativeAudioRef.current || audioRef.current;

    // Don't reload if it's the same URL we just loaded
    if (audioUrl === lastLoadedUrl && audio && audio.src && audio.readyState >= 2) {
      debugLog('[DEBUG] Audio URL unchanged and already loaded, skipping reload');
      // Ensure loading state is false and parent is notified
      if (isLoading) {
        setIsLoading(false);
      }
      return;
    }

    // Check if we already have this audio loaded (even if lastLoadedUrl doesn't match)
    if (audioUrl && audio && audio.currentSrc && audio.readyState >= 2) {
      const currentSrcUrl = audio.currentSrc;
      // For blob URLs, we can't easily compare, but for API URLs we can
      if (audioUrl.includes('/api/files/') && currentSrcUrl.includes('blob:')) {
        debugLog('[DEBUG] Audio appears to already be loaded as blob, checking if same file');
        // If we have a blob URL loaded and good readyState, assume it's the same file
        setLastLoadedUrl(audioUrl);
        // Ensure loading state is false and parent is notified
        if (isLoading) {
          setIsLoading(false);
        }
        return;
      }
    }

    const startLoadingProcess = async () => {
      debugLog('[DEBUG Starting audio loading process for:', audioUrl);

      // Get fresh audio element reference
      const currentAudio = imperativeAudioRef.current || audioRef.current;
      if (!currentAudio) {
        debugLog('[DEBUG Audio element not available for loading');
        setAudioError('Audio element not available. Please refresh the page.');
        setIsLoading(false);
        return;
      }

      // Set loading state to show loading indicator
      setIsLoading(true);
      setAudioError(null);

      // Clear any existing src first to avoid conflicts
      currentAudio.removeAttribute('src');
      currentAudio.src = '';

      debugLog('[DEBUG Audio element before reset:', {
        src: currentAudio.src,
        currentSrc: currentAudio.currentSrc,
        readyState: currentAudio.readyState,
        networkState: currentAudio.networkState
      });

      // Set up timeout for this specific load (reduced to 30 seconds)
      const timeout = setTimeout(() => {
        debugLog('[DEBUG Audio loading timeout check after 30 seconds');
        // Check current loading state at timeout time, not the closure value
        if (audio && !currentAudio.src && audioUrl) {
          debugLog('[DEBUG Audio loading timeout - no source set after 30 seconds');
          setAudioError('Audio loading timed out. Please try refreshing the page or try a different file.');
          setIsLoading(false);
        } else if (audio && currentAudio.readyState < 2 && audioUrl) {
          debugLog('[DEBUG Audio loading timeout - readyState:', currentAudio.readyState);
          setAudioError('Audio loading timed out. Please try refreshing the page or try a different file.');
          setIsLoading(false);
        } else {
          debugLog('[DEBUG Audio timeout fired but audio appears to be loaded, ignoring');
        }
      }, 30000);

      setLoadingTimeout(timeout);

      // Start the authenticated loading process
      try {
        setAudioError(null);
        const token = localStorage.getItem('authToken');

        let response;

        if (audioUrl.includes('/api/shared/')) {
          // Shared audio doesn't require authentication
          debugLog('[DEBUG Loading shared audio without auth:', audioUrl);
          response = await fetch(audioUrl);
        } else if (audioUrl.includes('/api/files/') && token) {
          // Private audio requires authentication
          debugLog('[DEBUG Loading authenticated audio:', audioUrl);
          debugLog('[DEBUG Using auth token:', token ? 'present' : 'missing');

          response = await fetch(audioUrl, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
        }

        if (response) {
          debugLog('[DEBUG Fetch response status:', response.status, response.ok);

          if (response.ok) {
            debugLog('[DEBUG Fetch successful, creating blob...');
            const audioBlob = await response.arrayBuffer();
            const blob = new Blob([audioBlob], { type: 'audio/mpeg' });

            debugLog('[DEBUG Blob created:', { size: blob.size, type: blob.type });

            // Create blob URL
            const blobUrl = URL.createObjectURL(blob);

            if (!blobUrl || !blobUrl.startsWith('blob:')) {
              debugLog('[DEBUG Failed to create blob URL');
              setAudioError('Failed to create audio URL. Please try refreshing the page.');
              setIsLoading(false);
              return;
            }

            debugLog('[DEBUG Audio blob URL created successfully:', blobUrl);

            // Verify blob URL
            if (!blobUrl || blobUrl === 'blob:' || !blobUrl.includes('blob:')) {
              debugLog('[DEBUG Invalid blob URL created:', blobUrl);
              setAudioError('Invalid audio URL created. Please refresh the page and try again.');
              setIsLoading(false);
              return;
            }

            debugLog('[DEBUG Blob URL validation passed');
            debugLog('[DEBUG Setting currentAudio.src to:', blobUrl);

            // Set the blob URL to the audio element
            const setBlobUrlToAudio = (audioElement, blobUrl) => {
              debugLog('[DEBUG Setting blob URL to audio element:', {
                blobUrl,
                audioElementExists: !!audioElement,
                audioElementTagName: audioElement?.tagName,
                audioElementId: audioElement?.id
              });

              audioElement.src = blobUrl;
              debugLog('[DEBUG Audio source set successfully:', audioElement.src === blobUrl);

              // Verify the source was actually set correctly
              if (!audioElement.src || !audioElement.src.startsWith('blob:')) {
                debugLog('[DEBUG Audio source was not set correctly!');
                debugLog('[DEBUG Expected blob URL starting with "blob:"');
                debugLog('[DEBUG Actual currentAudio.src:', audioElement.src);
                debugLog('[DEBUG Original blobUrl:', blobUrl);
                setAudioError('Failed to set audio source. Please try refreshing the page.');
                setIsLoading(false);
                return;
              }

              // Continue with the load process
              setTimeout(() => {
                const loadAudio = getAudioElementById();
                if (!loadAudio) {
                  debugLog('[DEBUG Audio element not found by ID during load() call');
                  setAudioError('Audio element lost during loading. Please refresh the page.');
                  setIsLoading(false);
                  return;
                }

                debugLog('[DEBUG Starting currentAudio.load() process...', {
                  src: loadAudio.src,
                  readyState: loadAudio.readyState,
                  networkState: loadAudio.networkState,
                  error: loadAudio.error
                });

                logAudioState('before load()');

                // Add event listeners for debugging
                const handleLoadStart = () => debugLog('[DEBUG Load started successfully');
                const handleCanPlay = () => {
                  debugLog('[DEBUG Audio can play - finishing loading');
                  setIsLoading(false);
                  setAudioError(null);
                  setLastLoadedUrl(audioUrl);
                };
                const handleError = (e) => {
                  debugLog('[DEBUG Audio loading error:', e);
                  if (loadAudio.error) {
                    debugLog('[DEBUG MediaError:', {
                      code: loadAudio.error.code,
                      message: loadAudio.error.message
                    });
                  }
                };

                // Add temporary event listeners
                loadAudio.addEventListener('loadstart', handleLoadStart, { once: true });
                loadAudio.addEventListener('canplay', handleCanPlay, { once: true });
                loadAudio.addEventListener('error', handleError, { once: true });

                loadAudio.load();
                debugLog('[DEBUG Audio.load() called');

                // Set up fallback check
                setTimeout(() => {
                  const fallbackAudio = getAudioElementById();
                  if (fallbackAudio && isLoading && fallbackAudio.readyState >= 2) {
                    debugLog('[DEBUG Fallback: finishing loading after 2 seconds');
                    setIsLoading(false);
                    setAudioError(null);
                    setLastLoadedUrl(audioUrl);

                    if (fallbackAudio.duration && isFinite(fallbackAudio.duration)) {
                      setDuration(fallbackAudio.duration);
                    }
                  } else if (isLoading) {
                    debugLog('[DEBUG Fallback check: still loading or not ready', {
                      isLoading,
                      readyState: fallbackAudio?.readyState,
                      src: fallbackAudio?.src,
                      error: fallbackAudio?.error
                    });
                  }
                }, 3000);
              }, 250);
            };

            // Set blob URL to audio with delay
            setTimeout(() => {
              const currentAudio = getAudioElementById();
              if (!currentAudio) {
                debugLog('[DEBUG Audio element not found by ID during blob setting - waiting longer...');
                setTimeout(() => {
                  const retryAudio = getAudioElementById();
                  if (!retryAudio) {
                    debugLog('[DEBUG Audio element still not found after retry');
                    setAudioError('Audio element lost during loading. Please refresh the page.');
                    setIsLoading(false);
                    return;
                  }
                  setBlobUrlToAudio(retryAudio, blobUrl);
                }, 500);
                return;
              }
              setBlobUrlToAudio(currentAudio, blobUrl);
            }, 200);

          } else {
            console.error('Failed to load authenticated audio:', response.status, response.statusText);
            let errorMessage = 'Failed to load audio file';
            try {
              const errorText = await response.text();
              console.error('Error response:', errorText);

              if (response.status === 404) {
                errorMessage = 'Audio file not found. It may have been deleted or moved.';
              } else if (response.status === 401 || response.status === 403) {
                errorMessage = 'Access denied. Please log in again or check your permissions.';
              } else if (response.status >= 500) {
                errorMessage = 'Server error. Please try again in a moment.';
              } else {
                errorMessage = `Audio loading failed (${response.status}). Please try refreshing the page.`;
              }
            } catch (parseError) {
              console.error('Could not parse error response:', parseError);
            }

            setAudioError(errorMessage);
            setIsLoading(false);
          }
        } else if (!audioUrl.includes('/api/')) {
          // For non-API URLs (direct URLs, blob URLs, etc), set directly
          debugLog('[DEBUG Setting direct audio source:', audioUrl);
          currentAudio.src = audioUrl;
          currentAudio.load();
          // Don't set isLoading to false here - wait for canplaythrough event
        } else {
          setAudioError('Authentication required to play this audio file.');
          setIsLoading(false);
          if (timeout) {
            clearTimeout(timeout);
          }
        }
      } catch (error) {
        console.error('Error loading authenticated audio:', error);
        const errorMessage = 'Error loading audio: ' + error.message;
        setAudioError(errorMessage);
        playErrorNotification();
        showBrowserNotification('Audio Loading Error', errorMessage, 'error');
        setIsLoading(false);
        if (timeout) {
          clearTimeout(timeout);
        }
      }
    };

    // New URL detected - start loading process
    if (audioUrl !== lastLoadedUrl) {
      debugLog('[DEBUG New audio URL detected, starting loading process:', audioUrl);
      debugLog('[DEBUG Previous URL was:', lastLoadedUrl);
      setAudioError(null);
      setIsLoading(true);

      // Wait for audio element to be created, then start loading
      waitForAudioElement();
    }

  }, [
    audioUrl,
    lastLoadedUrl
  ]);

  // Update audio current time when currentTime prop changes
  useEffect(() => {
    if (audioRef.current &&
        typeof currentTime === 'number' &&
        isFinite(currentTime) &&
        currentTime >= 0 &&
        Math.abs(audioRef.current.currentTime - currentTime) > 0.1) {
      audioRef.current.currentTime = currentTime;
    }
  }, [currentTime]);

  // Handle audio time updates
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current && onTimeUpdate) {
      const currentTime = audioRef.current.currentTime;
      debugLog('[AudioPlayer] Time update:', {
        currentTime,
        duration: audioRef.current.duration,
        readyState: audioRef.current.readyState,
        paused: audioRef.current.paused
      });
      onTimeUpdate(currentTime);
    }
  }, [onTimeUpdate]);

  // Create a function to attach event handlers to the audio element
  const attachEventHandlers = useCallback((audio) => {
    if (!audio) return;

    debugLog('[DEBUG Manually attaching duration event handlers to audio element:', audio.id);

    const handleDurationEvents = () => {
      debugLog('[DEBUG Duration event fired, duration:', audio.duration);
      if (audio.duration && isFinite(audio.duration)) {
        debugLog('[DEBUG Setting duration to:', audio.duration);
        setDuration(audio.duration);
      }
    };

    // Remove any existing listeners first
    audio.removeEventListener('loadedmetadata', handleDurationEvents);
    audio.removeEventListener('durationchange', handleDurationEvents);
    audio.removeEventListener('canplay', handleDurationEvents);

    // Add the duration event listeners
    audio.addEventListener('loadedmetadata', handleDurationEvents);
    audio.addEventListener('durationchange', handleDurationEvents);
    audio.addEventListener('canplay', handleDurationEvents);

    debugLog('[DEBUG Duration event handlers attached');
  }, []);

  // Handle audio metadata loaded (legacy - kept for compatibility)
  const handleLoadedMetadata = useCallback(() => {
    debugLog('[DEBUG Legacy handleLoadedMetadata called');
  }, []);

  // Handle play/pause
  const togglePlayPause = useCallback(async () => {
    if (audioRef.current) {
      try {
        const audio = audioRef.current;
        debugLog('[DEBUG togglePlayPause called, audio paused state:', audio.paused);

        if (!audio.paused) {
          debugLog('[DEBUG Pausing audio');
          audio.pause();
          // setIsPlaying will be called by 'pause' event listener
        } else {
          debugLog('[DEBUG Attempting to play audio');
          debugLog('[DEBUG Audio ready state before play:', audio.readyState);
          debugLog('[DEBUG Audio duration before play:', audio.duration);

          await audio.play();
          // setIsPlaying will be called by 'play' event listener
          debugLog('[DEBUG Audio play successful');
        }
      } catch (error) {
        debugLog('[DEBUG Audio play failed:', error);
        const errorMessage = 'Failed to play audio. The file may be corrupted or in an unsupported format.';
        setAudioError(errorMessage);
        playErrorNotification();
        showBrowserNotification('Audio Playback Failed', errorMessage, 'error');
        setIsPlaying(false);
      }
    }
  }, []);

  // Handle seek
  const handleSeek = useCallback((event) => {
    if (audioRef.current && duration > 0) {
      const rect = event.currentTarget.getBoundingClientRect();
      const percent = (event.clientX - rect.left) / rect.width;
      const newTime = percent * duration;
      if (isFinite(newTime) && newTime >= 0 && newTime <= duration) {
        audioRef.current.currentTime = newTime;
        onTimeUpdate(newTime);
      }
    }
  }, [duration, onTimeUpdate]);

  // Handle volume change
  const handleVolumeChange = useCallback((event) => {
    const newVolume = parseFloat(event.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else {
      setIsMuted(false);
    }
  }, []);

  // Handle mute toggle
  const toggleMute = useCallback(() => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  }, [isMuted, volume]);

  // Handle playback rate change
  const handlePlaybackRateChange = useCallback((event) => {
    const newRate = parseFloat(event.target.value);
    setPlaybackRate(newRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
  }, []);

  // Skip forward/backward
  const skipTime = useCallback((seconds) => {
    if (audioRef.current && duration > 0) {
      const newTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
      if (isFinite(newTime) && newTime >= 0) {
        audioRef.current.currentTime = newTime;
        onTimeUpdate(newTime);
      }
    }
  }, [duration, onTimeUpdate]);

  // Download audio file
  const downloadAudio = useCallback(async () => {
    if (!audioUrl) return;

    // Try to get original filename or create a default one
    let filename = 'audio-download';
    if (audioFile && audioFile.name) {
      filename = audioFile.name;
    } else {
      // Extract extension from URL or default to .webm
      const urlParts = audioUrl.split('.');
      const extension = urlParts.length > 1 ? urlParts[urlParts.length - 1].split('?')[0] : 'webm';
      filename = `audio-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.${extension}`;
    }

    // If it's a blob URL or external URL, download directly
    if (audioUrl.startsWith('blob:') || audioUrl.startsWith('http://') || audioUrl.startsWith('https://')) {
      const a = document.createElement('a');
      a.href = audioUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }

    // If it's an API endpoint, use the download endpoint that forces download
    try {
      const token = localStorage.getItem('authToken');

      // Convert /api/files/{hash}/audio to /api/files/{hash}/audio/download
      let downloadUrl = audioUrl;
      if (audioUrl.includes('/api/files/') && audioUrl.includes('/audio')) {
        downloadUrl = audioUrl.replace('/audio', '/audio/download');
      }

      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(downloadUrl, { headers });
      if (!response.ok) {
        throw new Error('Failed to download audio');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up blob URL
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Failed to download audio:', error);
      alert('Failed to download audio file. Please try again.');
    }
  }, [audioUrl, audioFile]);

  // Format time display
  const formatTime = useCallback((seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Audio loading function
  const startAudioLoading = useCallback(async (audio, audioUrl) => {
    debugLog('[DEBUG Starting audio loading process for:', audioUrl);
    logAudioState('load process start');

    // Clear any existing timeout first
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
    }

    // Set up timeout for this specific load (reduced to 30 seconds)
    const timeout = setTimeout(() => {
      debugLog('[DEBUG Audio loading timeout check after 30 seconds');
      // Check current loading state at timeout time, not the closure value
      if (audioRef.current && !audioRef.current.src && audioUrl) {
        debugLog('[DEBUG Audio loading timeout - no source set after 30 seconds');
        setAudioError('Audio loading timed out. Please try refreshing the page or try a different file.');
        setIsLoading(false);
      } else if (audioRef.current && audioRef.current.readyState < 2 && audioUrl) {
        debugLog('[DEBUG Audio loading timeout - readyState:', audioRef.current.readyState);
        setAudioError('Audio loading timed out. Please try refreshing the page or try a different file.');
        setIsLoading(false);
      } else {
        debugLog('[DEBUG Audio timeout fired but audio appears to be loaded, ignoring');
      }
    }, 30000);

    setLoadingTimeout(timeout);

    let blobUrl = null;

    // For authenticated endpoints, we need to use fetch with auth headers
    // then create a blob URL for the audio element
    const loadAuthenticatedAudio = async () => {
      try {
        setAudioError(null);
        const token = localStorage.getItem('authToken');

        // Reset audio element state before loading new source
        currentAudio.pause();
        currentAudio.currentTime = 0;

        debugLog('[DEBUG Audio element before reset:', {
          src: currentAudio.src,
          currentSrc: currentAudio.currentSrc,
          readyState: currentAudio.readyState,
          networkState: currentAudio.networkState
        });

        currentAudio.removeAttribute('src');
        currentAudio.src = ''; // Clear any existing src
        setIsPlaying(false);
        setDuration(0);

        debugLog('[DEBUG Audio element after reset:', {
          src: currentAudio.src,
          currentSrc: currentAudio.currentSrc,
          readyState: currentAudio.readyState
        });

        let response;

        if (audioUrl.includes('/api/shared/')) {
          // Shared audio doesn't require authentication
          debugLog('[DEBUG Loading shared audio without auth:', audioUrl);
          response = await fetch(audioUrl);
        } else if (audioUrl.includes('/api/files/') && token) {
          // Private audio requires authentication
          debugLog('[DEBUG Loading authenticated audio:', audioUrl);
          debugLog('[DEBUG Using auth token:', token ? 'present' : 'missing');

          response = await fetch(audioUrl, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
        }

        if (response) {
          debugLog('[DEBUG Fetch response status:', response.status, response.ok);

          if (response.ok) {
            debugLog('[DEBUG Fetch successful, creating blob...');
            const blob = await response.blob();
            debugLog('[DEBUG Blob created:', {
              size: blob.size,
              type: blob.type,
              constructor: blob.constructor.name
            });

            // Validate blob before creating URL
            if (blob.size === 0) {
              debugLog('[DEBUG Received empty blob for audio file');
              setAudioError('Audio file is empty or corrupted');
              setIsLoading(false);
              return;
            }

            // Clean up previous blob URL before creating new one
            if (currentAudio.src && currentAudio.src.startsWith('blob:')) {
              debugLog('[DEBUG Cleaning up previous blob URL:', currentAudio.src);
              URL.revokeObjectURL(currentAudio.src);
            }

            // Create blob URL with error handling
            try {
              blobUrl = URL.createObjectURL(blob);
              debugLog('[DEBUG Audio blob URL created successfully:', blobUrl);

              // Validate the created blob URL
              if (!blobUrl || !blobUrl.startsWith('blob:')) {
                throw new Error('Invalid blob URL created');
              }

              debugLog('[DEBUG Blob URL validation passed');
            } catch (blobError) {
              debugLog('[DEBUG Failed to create blob URL:', blobError);
              setAudioError('Failed to create audio blob URL. Please try refreshing the page.');
              setIsLoading(false);
              return;
            }

            // Set src and load with error handling
            debugLog('[DEBUG Setting currentAudio.src to:', blobUrl);

            // Validate blob URL before setting
            if (!blobUrl.startsWith('blob:')) {
              debugLog('[DEBUG Invalid blob URL detected:', blobUrl);
              setAudioError('Invalid audio URL generated. Please try refreshing the page.');
              setIsLoading(false);
              return;
            }

            // Clear any existing src first to avoid conflicts
            currentAudio.removeAttribute('src');
            currentAudio.src = '';

            // Longer delay to ensure DOM element is fully rendered
            setTimeout(() => {
              const currentAudio = getAudioElementById();
              if (!currentAudio) {
                debugLog('[DEBUG Audio element not found by ID during blob setting - waiting longer...');
                // Try again with more time for DOM to settle
                setTimeout(() => {
                  const retryAudio = getAudioElementById();
                  if (!retryAudio) {
                    debugLog('[DEBUG Audio element still not found after retry');
                    setAudioError('Audio element lost during loading. Please refresh the page.');
                    setIsLoading(false);
                    return;
                  }

                  // Proceed with blob setting using retry element
                  setBlobUrlToAudio(retryAudio, blobUrl);
                }, 500);
                return;
              }

              // Proceed with blob setting
              setBlobUrlToAudio(currentAudio, blobUrl);
            }, 200);

            // Helper function to set blob URL
            const setBlobUrlToAudio = (audioElement, blobUrl) => {
              debugLog('[DEBUG Setting blob URL to audio element:', {
                blobUrl,
                audioElementExists: !!audioElement,
                audioElementTagName: audioElement?.tagName,
                audioElementId: audioElement?.id
              });

              audioElement.src = blobUrl;
              debugLog('[DEBUG Audio source set successfully:', audioElement.src === blobUrl);

              // Verify the source was actually set correctly
              if (!audioElement.src || !audioElement.src.startsWith('blob:')) {
                debugLog('[DEBUG Audio source was not set correctly!');
                debugLog('[DEBUG Expected blob URL starting with "blob:"');
                debugLog('[DEBUG Actual currentAudio.src:', audioElement.src);
                debugLog('[DEBUG Original blobUrl:', blobUrl);
                setAudioError('Failed to set audio source. Please try refreshing the page.');
                setIsLoading(false);
                return;
              }

              // Continue with the load process
              setTimeout(() => {
                const loadAudio = getAudioElementById();
                if (!loadAudio) {
                  debugLog('[DEBUG Audio element not found by ID during load() call');
                  setAudioError('Audio element lost during loading. Please refresh the page.');
                  setIsLoading(false);
                  return;
                }

                debugLog('[DEBUG Starting currentAudio.load() process...', {
                  src: loadAudio.src,
                  readyState: loadAudio.readyState,
                  networkState: loadAudio.networkState,
                  error: loadAudio.error
                });

                logAudioState('before load()');

                // Add event listeners for debugging
                const handleLoadStart = () => debugLog('[DEBUG Load started successfully');
                const handleCanPlay = () => {
                  debugLog('[DEBUG Audio can play - finishing loading');
                  setIsLoading(false);
                  setAudioError(null);
                  setLastLoadedUrl(audioUrl);
                };
                const handleError = (e) => {
                  debugLog('[DEBUG Audio loading error:', e);
                  if (loadAudio.error) {
                    debugLog('[DEBUG MediaError:', {
                      code: loadAudio.error.code,
                      message: loadAudio.error.message
                    });
                  }
                };

                // Add temporary event listeners
                loadAudio.addEventListener('loadstart', handleLoadStart, { once: true });
                loadAudio.addEventListener('canplay', handleCanPlay, { once: true });
                loadAudio.addEventListener('error', handleError, { once: true });

                loadAudio.load();
                debugLog('[DEBUG Audio.load() called');

                // Set up fallback check
                setTimeout(() => {
                  const fallbackAudio = getAudioElementById();
                  if (fallbackAudio && isLoading && fallbackAudio.readyState >= 2) {
                    debugLog('[DEBUG Fallback: finishing loading after 2 seconds');
                    setIsLoading(false);
                    setAudioError(null);
                    setLastLoadedUrl(audioUrl);

                    if (fallbackAudio.duration && isFinite(fallbackAudio.duration)) {
                      setDuration(fallbackAudio.duration);
                    }
                  } else if (isLoading) {
                    debugLog('[DEBUG Fallback check: still loading or not ready', {
                      isLoading,
                      readyState: fallbackAudio?.readyState,
                      src: fallbackAudio?.src,
                      error: fallbackAudio?.error
                    });
                  }
                }, 3000);
              }, 250);
            };

          } else {
            console.error('Failed to load authenticated audio:', response.status, response.statusText);
            let errorMessage = 'Failed to load audio file';
            try {
              const errorText = await response.text();
              console.error('Error response:', errorText);

              if (response.status === 404) {
                errorMessage = 'Audio file not found. It may have been deleted or moved.';
              } else if (response.status === 401 || response.status === 403) {
                errorMessage = 'Access denied. Please log in again or check your permissions.';
              } else if (response.status >= 500) {
                errorMessage = 'Server error. Please try again in a moment.';
              } else {
                errorMessage = `Audio loading failed (${response.status}). Please try refreshing the page.`;
              }
            } catch (parseError) {
              console.error('Could not parse error response:', parseError);
            }

            setAudioError(errorMessage);
            setIsLoading(false);
          }
        } else if (!audioUrl.includes('/api/')) {
          // For non-API URLs (direct URLs, blob URLs, etc), set directly
          debugLog('[DEBUG Setting direct audio source:', audioUrl);
          currentAudio.src = audioUrl;
          currentAudio.load();
          // Don't set isLoading to false here - wait for canplaythrough event
        } else {
          setAudioError('Authentication required to play this audio file.');
          setIsLoading(false);
          if (loadingTimeout) {
            clearTimeout(loadingTimeout);
            setLoadingTimeout(null);
          }
        }
      } catch (error) {
        console.error('Error loading authenticated audio:', error);
        const errorMessage = 'Error loading audio: ' + error.message;
        setAudioError(errorMessage);
        playErrorNotification();
        showBrowserNotification('Audio Loading Error', errorMessage, 'error');
        setIsLoading(false);
        if (timeout) {
          clearTimeout(timeout);
          setLoadingTimeout(null);
        }
      }
    };

    loadAuthenticatedAudio();

    // Cleanup function
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
      if (timeout) {
        clearTimeout(timeout);
      }
    };

    // Start the waiting process
    waitForAudioElement();

    // Cleanup function
    return () => {
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
    };
  }, [audioUrl, lastLoadedUrl, imperativeAudioRef]);


  // Handle audio events
  useEffect(() => {
    debugLog('[DEBUG Event handler useEffect running');
    const audio = imperativeAudioRef.current || audioRef.current || document.getElementById(audioElementId);
    if (!audio) {
      debugLog('[DEBUG No audio element found for event handlers');
      return;
    }
    debugLog('[DEBUG Attaching event handlers to audio element:', {
      id: audio.id,
      src: audio.src,
      readyState: audio.readyState,
      imperativeRef: !!imperativeAudioRef.current,
      regularRef: !!audioRef.current
    });

    const handlePlay = () => {
      setIsPlaying(true);
      logAudioState('play');
      if (onPlay) onPlay();
    };
    const handlePause = () => {
      setIsPlaying(false);
      logAudioState('pause');
      if (onPause) onPause();
    };
    const handleEnded = () => {
      setIsPlaying(false);
      logAudioState('ended');
      if (onPause) onPause();
    };
    const handleCanPlayThrough = () => {
      debugLog('[DEBUG Audio can play through - loading complete');
      logAudioState('canplaythrough');
      setIsLoading(false);
      setAudioError(null); // Clear any previous errors
      setLastLoadedUrl(audioUrl); // Mark this URL as successfully loaded

      // Try to get duration if not already set
      if (audioRef.current) {
        debugLog('[DEBUG Audio ready state:', audioRef.current.readyState);
        debugLog('[DEBUG Audio duration on canplaythrough:', audioRef.current.duration);
        debugLog('[DEBUG Audio current src:', audioRef.current.currentSrc);
        debugLog('[DEBUG Audio seekable ranges:', audioRef.current.seekable.length);
        debugLog('[DEBUG Audio buffered ranges:', audioRef.current.buffered.length);

        if (audioRef.current.duration && isFinite(audioRef.current.duration)) {
          setDuration(audioRef.current.duration);
        } else if (audioRef.current.seekable.length > 0) {
          // Fallback: use seekable range end if duration is not available
          const seekableEnd = audioRef.current.seekable.end(0);
          if (isFinite(seekableEnd) && seekableEnd > 0) {
            debugLog('[DEBUG Using seekable end as duration:', seekableEnd);
            setDuration(seekableEnd);
          }
        }
      }

      // Clear loading timeout since audio is ready
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
        setLoadingTimeout(null);
      }
    };

    const handleLoadStart = () => {
      debugLog('[DEBUG Audio load started');
      if (audio) {
        logAudioState('loadstart');
      } else {
        // debugLog('[AUDIO DEBUG loadstart: Audio element no longer available');
      }
      setAudioError(null); // Clear any previous errors
    };

    const handleCanPlay = () => {
      debugLog('[DEBUG Audio can play - basic loading complete');
      logAudioState('canplay');

      // If we're still loading and audio can play, finish loading
      if (isLoading && audioRef.current && audioRef.current.readyState >= 3) {
        debugLog('[DEBUG Audio ready enough, finishing loading from canplay');
        setIsLoading(false);
        setAudioError(null);
        setLastLoadedUrl(audioUrl); // Mark this URL as successfully loaded

        // Clear loading timeout
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
          setLoadingTimeout(null);
        }
      }

      if (audioRef.current && audioRef.current.duration && isFinite(audioRef.current.duration)) {
        debugLog('[DEBUG Setting duration from canplay event:', audioRef.current.duration);
        setDuration(audioRef.current.duration);
      }
    };

    const handleDurationChange = () => {
      debugLog('[DEBUG Duration change event fired');
      debugLog('[DEBUG Duration change - current duration:', audio.duration);
      debugLog('[DEBUG Duration change - isFinite:', isFinite(audio.duration));
      if (audio.duration && isFinite(audio.duration)) {
        debugLog('[DEBUG Duration changed, setting to:', audio.duration);
        setDuration(audio.duration);
      } else {
        debugLog('[DEBUG Duration change - invalid duration, not setting');
      }
    };

    const handleLoadedMetadataLocal = () => {
      debugLog('[DEBUG Audio metadata loaded, duration:', audio.duration);
      debugLog('[DEBUG Duration isFinite:', isFinite(audio.duration));
      debugLog('[DEBUG Duration isNaN:', isNaN(audio.duration));
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
        debugLog('[DEBUG Duration set to:', audio.duration);
      } else {
        debugLog('[DEBUG Duration not set - invalid value:', audio.duration);
      }
    };

    const handleLoadedData = () => {
      debugLog('[DEBUG Audio data loaded');
      if (audioRef.current && audioRef.current.duration && isFinite(audioRef.current.duration)) {
        debugLog('[DEBUG Setting duration from loadeddata event:', audioRef.current.duration);
        setDuration(audioRef.current.duration);
      }
    };
    const handleError = (e) => {
      console.error('Audio error during loading:', isLoading, e);
      if (audio) {
        logAudioState('error event');
      } else {
        // debugLog('[AUDIO DEBUG error event: Audio element no longer available');
      }
      console.error('Audio element error details:', {
        error: e.target?.error,
        networkState: e.target?.networkState,
        readyState: e.target?.readyState,
        currentSrc: e.target?.currentSrc,
        wasLoading: isLoading,
        lastLoadedUrl: lastLoadedUrl
      });

      let errorMsg;

      // If we were still loading, this is a loading failure, not a playback failure
      if (isLoading) {
        debugLog('[DEBUG Error occurred during loading phase');
        errorMsg = 'Audio loading failed. The file may be corrupted or there may be a connection issue. Please try again.';
      } else {
        // This is a playback failure after the audio was successfully loaded
        debugLog('[DEBUG Error occurred after loading was complete');
        errorMsg = 'Audio playback failed. The audio file may not exist or you may not have permission to access it. Please check your connection and try again.';

        if (audioUrl?.includes('.m4a')) {
          errorMsg = 'M4A playback failed. This may be due to codec compatibility issues. Try converting to MP3 or WAV format.';
        } else if (audioUrl?.startsWith('blob:')) {
          errorMsg = 'Audio playback failed. The audio file may have expired or become corrupted. Please try reloading the file.';
        } else if (audioUrl?.includes('/api/')) {
          errorMsg = 'Audio playback failed. The audio file may not exist or you may not have permission to access it. Please check your connection and try again.';
        }
      }

      setAudioError(errorMsg);
      setIsPlaying(false);
      setIsLoading(false);

      // Clear loading timeout since we have an error
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
        setLoadingTimeout(null);
      }
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('error', handleError);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadataLocal);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadataLocal);

      // Clear timeout when component unmounts or audio changes
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
    };
  }, [handleTimeUpdate, logAudioState, audioElementId, onPlay, onPause, audioElementReady]);

  // Create audio element imperatively and manage it outside React
  useEffect(() => {
    // Create the audio element once and never let React destroy it
    if (!imperativeAudioRef.current) {
      const audioElement = document.createElement('audio');
      audioElement.id = audioElementId;
      audioElement.preload = 'metadata';
      audioElement.crossOrigin = 'anonymous';
      audioElement.playsInline = true; // Required for iOS inline playback
      audioElement.setAttribute('playsinline', ''); // Also set as attribute for older iOS
      audioElement.style.display = 'none'; // Hidden by default

      // Append to document body to keep it stable
      document.body.appendChild(audioElement);

      // Store references
      imperativeAudioRef.current = audioElement;
      audioRef.current = audioElement;

      debugLog('[DEBUG Created imperative audio element:', {
        audioElementId,
        element: audioElement,
        timestamp: Date.now()
      });

      // Signal that audio element is ready
      setAudioElementReady(true);
    }

    // Cleanup on unmount
    return () => {
      if (imperativeAudioRef.current) {
        debugLog('[DEBUG Cleaning up imperative audio element');
        document.body.removeChild(imperativeAudioRef.current);
        imperativeAudioRef.current = null;
        audioRef.current = null;
      }
    };
  }, [audioElementId]);

  // Helper function to get audio element reliably
  const getAudioElementById = useCallback(() => {
    const element = imperativeAudioRef.current || document.getElementById(audioElementId);
    debugLog('[DEBUG Getting audio element by ID:', {
      audioElementId,
      elementFound: !!element,
      elementTagName: element?.tagName,
      fromImperativeRef: !!imperativeAudioRef.current
    });
    return element;
  }, [audioElementId]);

  if (!audioUrl) {
    return (
      <div className="audio-player disabled">
        <div className="player-message">
          <span>🎵</span>
          <p>Upload an audio file to enable playback</p>
        </div>
      </div>
    );
  }

  if (showError && audioError) {
    return (
      <div className="audio-player error">
        <div className="player-message">
          <span>⚠️</span>
          <p>{audioError}</p>
          <small>Supported formats: MP3, WAV, OGG work best across all browsers</small>
        </div>
      </div>
    );
  }


  // Calculate progress percentage with defensive checks to prevent negative values
  const progressPercent = duration > 0 && currentTime >= 0
    ? Math.max(0, Math.min(100, (currentTime / duration) * 100))
    : 0;

  return (
    <div className="audio-player">
      {/* Audio element is managed imperatively and attached to document.body */}
      
      <div className="player-controls">
        <div className="main-controls">
          <button
            className="control-btn skip-btn"
            onClick={() => skipTime(-skipDuration)}
            title={`Skip back ${skipDuration}s`}
          >
            <span className="skip-icon">⏪</span>
            <span className="skip-duration">-{skipDuration}s</span>
          </button>

          <button
            className="control-btn play-pause-btn"
            onClick={togglePlayPause}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? '⏸️' : '▶️'}
          </button>

          <button
            className="control-btn skip-btn"
            onClick={() => skipTime(skipDuration)}
            title={`Skip forward ${skipDuration}s`}
          >
            <span className="skip-icon">⏩</span>
            <span className="skip-duration">+{skipDuration}s</span>
          </button>
        </div>
      </div>

      <div className="progress-container">
        <div 
          className="progress-bar" 
          onClick={handleSeek}
          title="Click to seek"
        >
          <div 
            className="progress-fill" 
            style={{ width: `${progressPercent}%` }}
          />
          <div 
            className="progress-handle" 
            style={{ left: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className="secondary-controls">
        <div className="volume-control">
          <button 
            className="control-btn volume-btn" 
            onClick={toggleMute}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? '🔇' : volume > 0.5 ? '🔊' : '🔉'}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="volume-slider"
            title="Volume"
          />
        </div>

        <div className="time-display">
          <span className="current-time">{formatTime(currentTime)}</span>
          <span className="time-separator">/</span>
          <span className="total-time">{formatTime(duration)}</span>
        </div>

        <div className="speed-control">
          <label htmlFor="playback-rate">Speed:</label>
          <select
            id="playback-rate"
            value={playbackRate}
            onChange={handlePlaybackRateChange}
            className="speed-select"
          >
            <option value="0.5">0.5x</option>
            <option value="0.75">0.75x</option>
            <option value="1">1x</option>
            <option value="1.25">1.25x</option>
            <option value="1.5">1.5x</option>
            <option value="2">2x</option>
            <option value="2.5">2.5x</option>
            <option value="3">3x</option>
            <option value="4">4x</option>
            <option value="5">5x</option>
          </select>
        </div>

        <div className="download-control">
          <button 
            className="control-btn download-btn" 
            onClick={downloadAudio}
            title="Download Audio"
          >
            💾
          </button>
        </div>
      </div>
      {audioFile && (
        <div className="audio-info">
          <span className="file-name">Now playing: {audioFile.name}</span>
        </div>
      )}
    </div>
  );
});

export default AudioPlayer;